import React, { useState, useEffect, useMemo } from 'react';

import 'src/app/popup.scss';
import styles from 'src/app/pages/Administration/tabs/EventTab/Event.module.scss';

import {
  actionService,
  eventService,
  incidentFolderService,
  incidentService,
  injectionService,
  observationTaskService,
  outcomeService,
  objectiveService,
  userService,
  workPackageService,
  taskService,
  projectInputService,
  kanbanService,
  deliverableService,
  testService,
  projectService,
} from 'src/app/services';
import { useDispatch, useSelector } from 'react-redux';
import { reduxRootTypes } from 'src/types';
import { contentsActions, eventActions, userActions } from 'src/app/redux/actions';

import { EventDataType, EventResultType } from 'src/types/services/events-service.type';
import { storageHelper } from 'src/app/helpers';

import EventForm from 'src/app/components/Forms/EventForm/eventForm';
import moment from 'moment';

import { Item, EventPropsType, deleteConfirmTitles } from './event.config';
import { displaySuccessStatusMessage } from 'src/app/helpers/status.helper';
import { checkPermissionList } from 'src/app/helpers/permissions.helper';
import { PermissionEnum } from 'src/enums/permission.enum';
import useSearch from 'src/app/hooks/useSearch';
import CustomTable from 'src/app/components/CustomTable/CustomTable';
import useLogout from 'src/app/hooks/useLogout';
import { SettingResultType } from 'src/types/services/setting-service.type';

import { LuKanbanSquare } from 'react-icons/lu';
import Kanban from 'src/app/components/Kanban/Kanban';
import { axios } from 'src/app/utils';
import { allPPMReadPermissions } from 'src/app/components/Sidebar/sidebar.config';
import { EventTimePreference } from 'src/enums/eventTimePreference.enum';
import { useTranslation } from 'react-i18next';
import { isCurrentUserAdminInDatabase } from 'src/app/helpers/isUserOperationsDisabled.helper';

import Input from 'antd/es/input';
import Form from 'antd/es/form';
import Popover from 'antd/es/popover';
import Typography from 'antd/es/typography';
import Popconfirm from 'antd/es/popconfirm';
import Tooltip from 'antd/es/tooltip';
import Button from 'antd/es/button/button';
import Spin from 'antd/es/spin';

const { Search } = Input;

const EventTab: React.FC<EventPropsType> = (props: EventPropsType) => {
  const { t } = useTranslation();

  const defaultEventData: EventDataType = {
    id: '',
    eventId: '',
    eventName: '',
    eventClassification: '',
    eventTypeId: '',
    audience: '',
    timeplanTemplateId: '',
    eventColor: '#000000',
    anchorEventTime: undefined,
    OSEId: '',
    OCEId: '',
    ODE: '',
    direvalId: '',
    organizationTemplate: '',
    ETE3EventObjectives: '',
    anchorEventLocationId: '',
    hymotsDB: undefined,
    settingId: '',
    selectedEventId: undefined,
    timePreference: EventTimePreference.Real,
    defaultRlsmAdminUserId: undefined,
  };

  const dispatch = useDispatch();

  const [dataForEvent, setDataForEvent] = useState<Item[]>([]);
  const [eventAllData, setEventAllData] = useState<EventResultType[]>([]);

  const [eventDataInForm, setEventDataInForm] = useState<EventDataType>(defaultEventData);

  const { searchValue, onSearch } = useSearch();

  const [addEventModal, setAddEventModal] = useState<boolean>(false);
  const [modalInEditMode, setModalInEditMode] = useState<boolean>(false);

  const [showKanbanModal, setShowKanbanModal] = useState<boolean>(false);

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [kanbanEventId, setKanbanEventId] = useState<string>('');

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { ontologies } = useSelector((state: reduxRootTypes.RootStateType) => state.ontologyState);
  const userState = useSelector((state: reduxRootTypes.RootStateType) => state.userState);
  const { eventList, eventId } = useSelector((state: reduxRootTypes.RootStateType) => state.eventState);
  const { localpermissions } = useSelector((state: reduxRootTypes.RootStateType) => state.userRolePermissionState);

  const logout = useLogout();
  const [form] = Form.useForm();
  const lastVisitedKey = eventDataInForm.id || '';

  const currentUserAdminEvents = useMemo(() => {
    return userState.events.filter((e) => e.roles.some((r) => r.abbreviation === 'Admin'));
  }, [userState, eventList]);

  const onClickAddPlayerType = () => {
    if (modalInEditMode) {
      setEventDataInForm(defaultEventData);
    }
    setAddEventModal(true);
    setModalInEditMode(false);
  };

  useEffect(() => {
    const clearData: Item[] = [];

    eventAllData
      .filter((e) => userState.events.map((event) => event.id).includes(e.id))
      .forEach((item) => {
        clearData.push({
          id: item.id,
          eventId: item.eventId,
          eventName: item.eventName,
          eventTypeId: ontologies.find((element) => item.eventTypeId === element.id)?.abbreviation || '',
          audience: item.audience,
          timeplanTemplateId: ontologies.find((element) => item.timeplanTemplateId === element.id)?.abbreviation || '',
          eventColor: item.eventColor,
          anchorEventTime: item.anchorEventTime,
          OSEId: ontologies.find((element) => item.OSEId === element.id)?.abbreviation || '',
          OCEId: ontologies.find((element) => item.OCEId === element.id)?.abbreviation || '',
          ODE: ontologies.find((element) => item.ODE === element.id)?.abbreviation || '',
          direvalId: ontologies.find((element) => item.direvalId === element.id)?.abbreviation || '',
          organizationTemplate: item.organizationTemplate,
          ETE3EventObjectives: item.ETE3EventObjectives,
          timePreference: item.timePreference,

          anchorEventLocationId:
            ontologies.find((element) => item.anchorEventLocationId === element.id)?.abbreviation || '',
          hymotsDB: item.hymotsDB,
        });
      });
    setDataForEvent(clearData);
  }, [eventAllData, userState.events]);

  const getData = async () => {
    try {
      const lowerSearchValue = searchValue.toLowerCase();

      if (lowerSearchValue.length > 0) {
        const searchEventList = eventList.filter((obj) => {
          const fieldsToCheck = [
            obj.eventId,
            obj.eventName,
            obj.audience,
            obj.eventColor,
            obj.organizationTemplate,
            obj.ETE3EventObjectives,
          ];

          return fieldsToCheck.some((field) => field?.toString().toLowerCase().includes(lowerSearchValue));
        });

        setEventAllData(searchEventList);
      } else {
        setEventAllData(eventList);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getData();
  }, [searchValue, eventList]);

  const onClickShow = (record: Partial<Item> & { id: React.Key }) => {
    setAddEventModal(true);
    setModalInEditMode(true);
  };

  const edit = (record: Partial<Item> & { id: React.Key }) => {
    const item = eventAllData.find((o) => o.id === record.id);
    if (item) {
      onClickShow(record);
      setEventDataInForm({
        ...item,
        anchorEventTime: item.anchorEventTime ? moment(item.anchorEventTime) : item.anchorEventTime,
      });

      setModalInEditMode(true);
    }
  };

  const deleteEvent = async (deletedItem: Item) => {
    try {
      await eventService.eventDelete({
        id: deletedItem.id.toString(),
      });
      if (deletedItem.id === eventDataInForm.id) {
        setEventDataInForm(defaultEventData);
        // TODO açık olan popup sayfasının kapanması için değişkeni ekle.
      }
      if (deletedItem.id === eventId) {
        logout();
        displaySuccessStatusMessage(t('success.deleted'));
        return;
      }

      dispatch(eventActions.eventDeleted(deletedItem.id));
      displaySuccessStatusMessage(t('success.deleted'));

      const {
        data: { rows: trainingObjectiveListData },
      } = await objectiveService.lookupTrainingObjectiveTable({ blockSpinner: true });

      const {
        data: { rows: incidentFolderData },
      } = await incidentFolderService.lookupIncidentFolder({ blockSpinner: true });

      const {
        data: { rows: incidentData },
      } = await incidentService.lookupIncidentTable({ blockSpinner: true });

      const {
        data: { rows: injectionData },
      } = await injectionService.lookupInjectionTable({ blockSpinner: true });

      const {
        data: { rows: actionData },
      } = await actionService.lookupActionTable({ blockSpinner: true });

      const {
        data: { rows: outcomeData },
      } = await outcomeService.lookupOutcomeTable({ blockSpinner: true });

      const {
        data: { rows: observationTaskData },
      } = await observationTaskService.lookupObservationTaskTable({ blockSpinner: true });

      const {
        data: { rows: userData },
      } = await userService.lookupUsersTable({ blockSpinner: true });

      dispatch(contentsActions.trainingObjectiveList(trainingObjectiveListData));
      dispatch(contentsActions.incidentFolderList(incidentFolderData));
      dispatch(contentsActions.incidentList(incidentData));
      dispatch(contentsActions.injectionList(injectionData));
      dispatch(contentsActions.actionList(actionData));
      dispatch(contentsActions.outcomeList(outcomeData));
      dispatch(contentsActions.observationTaskList(observationTaskData));
      dispatch(userActions.setListInformation(userData));
    } catch (error) {
      console.log(error);
    }
  };

  const changeEvent = async (eventId: string) => {
    const { event } = (await eventService.eventSelect({ id: eventId })).data;
    if (event) {
      dispatch(contentsActions.configurationChangeRequestList([]));
      dispatch(contentsActions.troubleTicketList([]));
      axios.setEventId(event.id);

      storageHelper.setValueWithKey('eventId', event.id.toString());
      storageHelper.setValueWithKey('veritabaniId', event.hymotsDB?.toString()!);

      dispatch(
        eventActions.eventDataChanged({
          eventClassification: event.eventClassification,
          eventName: event.eventId,
          hymotsDB: event.hymotsDB,
          hlaContent: undefined,
        }),
      );

      dispatch(eventActions.eventIdChanged(event.id));
      dispatch(contentsActions.roadToCurrentSettingsList([]));
    }
  };

  const fetchKanbanDataForDifferentEvent = async (eventId: string) => {
    const projectsData = await projectService.lookupProjectWithEvent({ eventIds: eventId });
    dispatch(contentsActions.projectsList(projectsData.data.rows));

    const workPackageData = await workPackageService.lookupWorkPackageWithEvent({ eventIds: eventId });
    dispatch(contentsActions.workPackageList(workPackageData.data.rows));

    const taskData = await taskService.lookupTaskWithEvent({ eventIds: eventId });
    dispatch(contentsActions.taskList(taskData.data.rows));

    const projectInputData = await projectInputService.lookupProjectInputWithEvent({ eventIds: eventId });
    dispatch(contentsActions.projectInputList(projectInputData.data.rows));

    const kanban = await kanbanService.lookupKanbanWithEvent({ eventIds: eventId });
    dispatch(contentsActions.kanbanList(kanban.data.rows));

    const deliverable = await deliverableService.lookupDeliverableWithEvent({ eventIds: eventId });
    dispatch(contentsActions.deliverableList(deliverable.data.rows));

    const test = await testService.lookupTestWithEvent({ eventIds: eventId });
    dispatch(contentsActions.testList(test.data.rows));
  };

  const fetchAndResetToInitialData = async () => {
    const projectsData = await projectService.lookupProject({ blockSpinner: true });
    dispatch(contentsActions.projectsList(projectsData.data.rows));

    const workPackageData = await workPackageService.lookupWorkPackage({ blockSpinner: true });
    dispatch(contentsActions.workPackageList(workPackageData.data.rows));

    const taskData = await taskService.lookupTaskTable({ blockSpinner: true });
    dispatch(contentsActions.taskList(taskData.data.rows));

    const projectInputData = await projectInputService.lookupProjectInputTable({ blockSpinner: true });
    dispatch(contentsActions.projectInputList(projectInputData.data.rows));

    const kanban = await kanbanService.lookupKanbanTable({ blockSpinner: true });
    dispatch(contentsActions.kanbanList(kanban.data.rows));

    const deliverable = await deliverableService.lookupDeliverableTable({ blockSpinner: true });
    dispatch(contentsActions.deliverableList(deliverable.data.rows));

    const test = await testService.lookupTestTable({ blockSpinner: true });
    dispatch(contentsActions.testList(test.data.rows));
  };

  const handleShowKanban = async (record: Partial<Item> & { id: React.Key }) => {
    // kanbanEventId is used for request optimization. If the user clicks already selected event, we don't need to fetch all data again.
    setKanbanEventId(storageHelper.getValueWithKey('eventId') ?? '');
    if (record.id.toString() !== storageHelper.getValueWithKey('eventId')) {
      setIsLoading(true);

      // If the user clicks a different event, we need to fetch all data again.
      setKanbanEventId(record.id.toString());

      await fetchKanbanDataForDifferentEvent(record.id.toString());

      setIsLoading(false);
      setShowKanbanModal(true);
    } else {
      setShowKanbanModal(true);
    }
  };

  const handleCloseKanban = async () => {
    // If the user clicks a different event, we need to fetch all data again.
    if (kanbanEventId !== storageHelper.getValueWithKey('eventId')) {
      fetchAndResetToInitialData();
    }

    setShowKanbanModal(false);
  };

  const columns = [
    {
      title: t('general.eventId'),
      dataIndex: 'eventId',
      width: '20%',
    },
    {
      title: t('administration.event.eventName'),
      dataIndex: 'eventName',
      width: '25%',
      render: (value: any, record: any) => {
        return (
          <Popover content={record.eventName} trigger="hover">
            <div className={styles.tableTitleStyle}>{record.eventName}</div>
          </Popover>
        );
      },
    },
    {
      title: t('administration.event.eventType'),
      dataIndex: 'eventTypeId',
      width: '12%',
    },
    {
      title: t('administration.event.audience'),
      dataIndex: 'audience',
      width: '15%',
      render: (value: any, record: any) => {
        return (
          <Popover content={record.audience} trigger="hover">
            <div className={styles.tableTitleStyle}>{record.audience}</div>
          </Popover>
        );
      },
    },
    {
      title: t('administration.event.timeplanTemplate'),
      dataIndex: 'timeplanTemplateId',
      width: '13%',
    },
    {
      title: t('administration.event.eventColor'),
      dataIndex: 'eventColor',
      width: '10%',
      render: (color?: string) => {
        const backgroundColor = /^#([0-9A-F]{3}){1,2}$/i.test(color || '') ? color : '#000000';
        return (
          <div
            style={{
              backgroundColor,
              width: '100%',
              height: '24px',
              borderRadius: '4px',
            }}
          />
        );
      },
    },
    {
      title: t('administration.event.anchorEventTime'),
      dataIndex: 'anchorEventTime',
      width: '23%',
      sorter: (a: any, b: any) => {
        const firstDate = moment(a.anchorEventTime, 'YYYY-MM-DD-HH:mm').unix();
        const secondDate = moment(b.anchorEventTime, 'YYYY-MM-DD-HH:mm').unix();
        return firstDate - secondDate;
      },
      render: (value: string) => {
        if (!value) return '';
        const formatted = moment(value).format('YYYY-MM-DD HH:mm');
        return formatted;
      },
    },
    {
      title: t('administration.event.oseId'),
      dataIndex: 'OSEId',
      width: '13%',
      render: (value: any, record: any) => {
        return (
          <Popover content={record.OSEId} trigger="hover">
            <div className={styles.tableTitleStyle}>{record.OSEId}</div>
          </Popover>
        );
      },
    },
    {
      title: t('administration.event.oceId'),
      dataIndex: 'OCEId',
      width: '13%',
      render: (value: any, record: any) => {
        return (
          <Popover content={record.OCEId} trigger="hover">
            <div className={styles.tableTitleStyle}>{record.OCEId}</div>
          </Popover>
        );
      },
    },
    {
      title: t('administration.event.ode'),
      dataIndex: 'ODE',
      width: '13%',
      render: (value: any, record: any) => {
        return (
          <Popover content={record.ODE} trigger="hover">
            <div className={styles.tableTitleStyle}>{record.ODE}</div>
          </Popover>
        );
      },
    },
    {
      title: t('administration.event.direval'),
      dataIndex: 'direvalId',
      width: '13%',
      render: (value: any, record: any) => {
        return (
          <Popover content={record.direvalId} trigger="hover">
            <div className={styles.tableTitleStyle}>{record.direvalId}</div>
          </Popover>
        );
      },
    },
    {
      title: t('administration.event.organizationTemplate'),
      dataIndex: 'organizationTemplate',
      width: '15%',
      render: (value: any, record: any) => {
        return (
          <Popover content={record.organizationTemplate} trigger="hover">
            <div className={styles.tableTitleStyle}>{record.organizationTemplate}</div>
          </Popover>
        );
      },
    },
    // {
    //   title: 'ETE3 Event Objectives',
    //   dataIndex: 'ETE3EventObjectives',
    //   width: '25%',
    //   render: (value: any, record: any) => {
    //     return (
    //       <Popover content={record.ETE3EventObjectives} trigger="hover">
    //         <div className={styles.tableTitleStyle}>{record.ETE3EventObjectives}</div>
    //       </Popover>
    //     );
    //   },
    // },
    {
      title: t('administration.event.anchorEventLocation'),
      dataIndex: 'anchorEventLocationId',
      width: '15%',
    },
    {
      title: t('general.operation.operation'),
      dataIndex: 'operation',
      width: '15%',
      fixed: 'right' as const,
      render: (_: any, record: Item) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {localpermissions.find((permission) => allPPMReadPermissions.includes(permission)) && (
              <Popover content={<div>{t('general.operation.kanbanView')}</div>} trigger="hover">
                <Typography.Link onClick={() => handleShowKanban(record)} className="flex-center">
                  <LuKanbanSquare style={{ fontSize: 23, marginLeft: 10 }} />
                </Typography.Link>
              </Popover>
            )}
            <Popover content={<div>{t('general.operation.edit')}</div>} trigger="hover">
              <Typography.Link
                onClick={() => edit(record)}
                disabled={
                  checkPermissionList(PermissionEnum.EventUpdate) ||
                  !isCurrentUserAdminInDatabase(userState, currentUserAdminEvents, record.id)
                }
                className="flex-center"
              >
                <img className="edit-icon" src="/assets/editIcon.png" alt="edit" />
              </Typography.Link>
            </Popover>
            <Popover content={<div>{t('general.operation.delete')}</div>} trigger="hover">
              <Popconfirm
                placement="bottom"
                title={deleteConfirmTitles().title}
                okText={deleteConfirmTitles().okText}
                cancelText={deleteConfirmTitles().cancelText}
                icon=""
                onCancel={() => deleteEvent(record)}
                disabled={
                  checkPermissionList(PermissionEnum.EventDelete) ||
                  !isCurrentUserAdminInDatabase(userState, currentUserAdminEvents, record.id)
                }
              >
                <img
                  className="delete-icon"
                  src="/assets/deleteIcon.png"
                  alt="delete"
                  aria-disabled={
                    checkPermissionList(PermissionEnum.EventDelete) ||
                    !isCurrentUserAdminInDatabase(userState, currentUserAdminEvents, record.id)
                  }
                />
              </Popconfirm>
            </Popover>
          </div>
        );
      },
    },
  ];

  const mergedColumns = columns.map((col) => {
    return {
      ...col,
      onCell: (record: Item) => ({
        record,
        inputType: col.dataIndex === 'type' ? 'number' : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
      }),
      title: (
        <Tooltip title={col.title}>
          <span className={styles.tableTitleStyle}>{col.title}</span>
        </Tooltip>
      ),
    };
  });

  const handleOnClickNext = () => {
    if (currentStep === 1) {
      setCurrentStep(0);
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  return (
    <>
      <div className="database-main-container">
        <div className="database-container">
          <div className="database-add-button-container">
            <Button
              className="database-add-button"
              onClick={onClickAddPlayerType}
              disabled={checkPermissionList(PermissionEnum.EventCreate)}
            >
              <div className="database-add-button-inner-container">
                <p className="database-add-button-text">{t('administration.event.addEvent')}</p>
              </div>
            </Button>
            <Search
              placeholder={t('general.search') || ''}
              allowClear
              onSearch={onSearch}
              onChange={onSearch}
              className="database-search-box"
            />
          </div>
          <Form form={form} component={false}>
            {isLoading && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div
                  style={{
                    width: '100vw',
                    height: '100vh',
                    zIndex: 1000,
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: 'rgba(63, 63, 63, 0.5)',
                  }}
                >
                  <Spin tip="Loading" style={{ color: 'var(--ete-primary-green-color)' }} size="large" />
                </div>
              </div>
            )}

            <CustomTable
              dataSource={dataForEvent}
              columns={mergedColumns}
              rowClassName={(record) => (record.id === lastVisitedKey.toString() ? 'last-visited-row' : '')}
            />
          </Form>
        </div>
      </div>
      {addEventModal && (
        <EventForm
          setEditModal={modalInEditMode}
          eventForm={eventDataInForm}
          onClickOpen
          setOnClickOpen={setAddEventModal}
        />
      )}
      {showKanbanModal && <Kanban setOnClickOpen={handleCloseKanban} />}
    </>
  );
};

export default EventTab;
